import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Logo from "../../../commonComponents/header/Logo";
import "./MatchTheFollowing.css";
/**
 * MatchTheFollowing
 * ------------------
 * A "connect the terms" matching exercise. Each left-hand term has an arrow
 * handle on its right edge that the user drags onto a right-hand term to form
 * a pair. Matching is strictly one-to-one:
 *   - a left term connects to at most one right term
 *   - a right term receives at most one left term (a new drop steals it)
 *
 * Props:
 *   title        - main heading
 *   subtitle     - instruction line
 *   context      - small amber context label
 *   left         - [{ id, label }]  (the prompts, with drag handles)
 *   right        - [{ id, label }]  (the targets)
 *   value        - controlled connections map { [leftId]: rightId } (optional)
 *   defaultValue - uncontrolled initial connections map (optional)
 *   onChange     - (connections) => void, fires on every change
 *   onSubmit     - (connections) => void, fires when "Submit Answer" is clicked
 *   disabled     - boolean, disables interactions
 */
export default function MatchTheFollowing({
  title = "A Quick Question! Read and Answer carefully.",
  subtitle = "Drag the arrow on each term to its matching definition on the right.Each term pairs with exactly one",
  context = "Context: Software Design and Patterns - Microservices",
  left = DEFAULT_LEFT,
  right = DEFAULT_RIGHT,
  value,
  defaultValue,
  onChange,
  onSubmit,
  disabled = false,
}) {
  // ---- connections state (controlled or uncontrolled) --------------------
  const isControlled = value != null;
  const [internal, setInternal] = useState(defaultValue || {});
  const connections = isControlled ? value : internal;
  const [showDemoPanel, setShowDemoPanel] = useState(false);
  const setConnections = useCallback(
    (updater) => {
      const next =
        typeof updater === "function" ? updater(connections) : updater;
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [connections, isControlled, onChange],
  );

  // ---- element registries for measuring anchor positions -----------------
  const containerRef = useRef(null);
  const leftHandleEls = useRef({}); // leftId  -> handle DOM node (right edge)
  const rightDotEls = useRef({}); // rightId -> dot DOM node (left edge)
  const rightRowEls = useRef({}); // rightId -> full row DOM node (hit testing)

  const setLeftHandle = useCallback(
    (id) => (el) => {
      if (el) leftHandleEls.current[id] = el;
      else delete leftHandleEls.current[id];
    },
    [],
  );
  const setRightDot = useCallback(
    (id) => (el) => {
      if (el) rightDotEls.current[id] = el;
      else delete rightDotEls.current[id];
    },
    [],
  );
  const setRightRow = useCallback(
    (id) => (el) => {
      if (el) rightRowEls.current[id] = el;
      else delete rightRowEls.current[id];
    },
    [],
  );

  // ---- measured anchor coordinates (relative to container) ---------------
  const [anchors, setAnchors] = useState({ left: {}, right: {} });
  const [tick, setTick] = useState(0); // bump to force re-measure

  const measure = useCallback(() => {
    const c = containerRef.current;
    if (!c) return;
    const cr = c.getBoundingClientRect();
    const next = { left: {}, right: {} };
    for (const [id, el] of Object.entries(leftHandleEls.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      next.left[id] = {
        x: r.left + r.width / 2 - cr.left,
        y: r.top + r.height / 2 - cr.top,
      };
    }
    for (const [id, el] of Object.entries(rightDotEls.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      next.right[id] = {
        x: r.left + r.width / 2 - cr.left,
        y: r.top + r.height / 2 - cr.top,
      };
    }
    setAnchors(next);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, tick, left, right, connections]);

  useLayoutEffect(() => {
    const c = containerRef.current;
    if (!c || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setTick((t) => t + 1));
    ro.observe(c);
    const onWin = () => setTick((t) => t + 1);
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, []);

  // ---- drag interaction --------------------------------------------------
  // drag = { leftId, x, y } while dragging (x/y relative to container)
  const [drag, setDrag] = useState(null);
  const [hoverRight, setHoverRight] = useState(null);

  const toLocal = useCallback((clientX, clientY) => {
    const c = containerRef.current;
    const cr = c.getBoundingClientRect();
    return { x: clientX - cr.left, y: clientY - cr.top };
  }, []);

  // which right row sits under the given viewport point?
  const hitTestRight = useCallback((clientX, clientY) => {
    for (const [id, el] of Object.entries(rightRowEls.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= r.top &&
        clientY <= r.bottom
      ) {
        return id;
      }
    }
    return null;
  }, []);

  const onHandleDown = useCallback(
    (e, leftId) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      // detach this left's existing pair so it can be re-routed
      if (connections[leftId] != null) {
        setConnections((prev) => {
          const n = { ...prev };
          delete n[leftId];
          return n;
        });
      }
      const p = toLocal(e.clientX, e.clientY);
      setDrag({ leftId, x: p.x, y: p.y });
      setHoverRight(null);
    },
    [connections, disabled, setConnections, toLocal],
  );

  const onHandleMove = useCallback(
    (e) => {
      if (!drag || disabled) return;
      const p = toLocal(e.clientX, e.clientY);
      setDrag((d) => (d ? { ...d, x: p.x, y: p.y } : d));
      setHoverRight(hitTestRight(e.clientX, e.clientY));
    },
    [disabled, drag, hitTestRight, toLocal],
  );

  const onHandleUp = useCallback(
    (e) => {
      if (!drag || disabled) return;
      const target = hitTestRight(e.clientX, e.clientY);
      if (target) {
        setConnections((prev) => {
          const n = { ...prev };
          // enforce one-to-one on the right: free the target first
          for (const k of Object.keys(n)) if (n[k] === target) delete n[k];
          n[drag.leftId] = target;
          return n;
        });
      }
      setDrag(null);
      setHoverRight(null);
    },
    [disabled, drag, hitTestRight, setConnections],
  );

  const clearPair = useCallback(
    (leftId) => {
      if (disabled) return;
      setConnections((prev) => {
        const n = { ...prev };
        delete n[leftId];
        return n;
      });
    },
    [disabled, setConnections],
  );

  // ---- derived lookups ---------------------------------------------------
  const rightToLeft = useMemo(() => {
    const m = {};
    for (const [l, r] of Object.entries(connections)) m[r] = l;
    return m;
  }, [connections]);

  const matchedCount = Object.keys(connections).length;

  // color per left item so a pair's line/handles/target share a hue
  const colorFor = useCallback(
    (leftId) => {
      const idx = left.findIndex((it) => it.id === leftId);
      return LINE_COLORS[(idx + LINE_COLORS.length) % LINE_COLORS.length];
    },
    [left],
  );

  // ---- render ------------------------------------------------------------
  return (
    <div className="mtf-root">
      <StyleOnce />
      <header className="mtf-head">
        <div className="mtf-head-top">
          <div className="mtf-logo-wrapper">
            <Logo />
          </div>
          <h1 className="mtf-title">{title}</h1>
          <button
            type="button"
            className="mtf-demo-btn"
            onClick={() => setShowDemoPanel(true)}
          >
            Click to see a demo
          </button>
        </div>
        {subtitle && <p className="mtf-subtitle">{subtitle}</p>}
        {/* {context && <p className="mtf-context">{context}</p>} */}
      </header>

      <div className="mtf-board" ref={containerRef}>
        {/* connection layer */}
        <svg className="mtf-svg" aria-hidden="true">
          <defs>
            {LINE_COLORS.map((c, i) => (
              <marker
                key={i}
                id={`mtf-arrow-${i}`}
                markerWidth="10"
                markerHeight="10"
                refX="7"
                refY="3"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M0,0 L7,3 L0,6 Z" fill={c} />
              </marker>
            ))}
          </defs>

          {/* committed pairs */}
          {Object.entries(connections).map(([leftId, rightId]) => {
            const a = anchors.left[leftId];
            const b = anchors.right[rightId];
            if (!a || !b) return null;
            const color = colorFor(leftId);
            const idx = LINE_COLORS.indexOf(color);
            return (
              <g key={leftId} className="mtf-pair">
                {/* wide invisible hit area to allow click-to-remove */}
                <path
                  d={curve(a, b)}
                  className="mtf-hit"
                  onClick={() => clearPair(leftId)}
                />
                <path
                  d={curve(a, b)}
                  className="mtf-line"
                  stroke={color}
                  markerEnd={`url(#mtf-arrow-${idx})`}
                />
              </g>
            );
          })}

          {/* live drag line */}
          {drag &&
            anchors.left[drag.leftId] &&
            (() => {
              const a = anchors.left[drag.leftId];
              const b = { x: drag.x, y: drag.y };
              return (
                <path
                  d={curve(a, b)}
                  className="mtf-line mtf-line--drag"
                  stroke={colorFor(drag.leftId)}
                />
              );
            })()}
        </svg>

        {/* two columns */}
        <div className="mtf-col mtf-col--left">
          {left.map((item) => {
            const connected = connections[item.id] != null;
            const color = colorFor(item.id);
            const active = drag?.leftId === item.id;
            return (
              <div
                key={item.id}
                className={
                  "mtf-cell mtf-cell--left" +
                  (connected ? " is-connected" : "") +
                  (active ? " is-active" : "")
                }
                style={connected || active ? { borderColor: color } : undefined}
              >
                <span className="mtf-label">{item.label}</span>
                <button
                  type="button"
                  className="mtf-handle"
                  ref={setLeftHandle(item.id)}
                  style={
                    connected || active
                      ? { background: color, borderColor: color }
                      : undefined
                  }
                  onPointerDown={(e) => onHandleDown(e, item.id)}
                  onPointerMove={onHandleMove}
                  onPointerUp={onHandleUp}
                  aria-label={`Drag to connect ${item.label}`}
                  title="Drag to a term on the right"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path
                      d="M5 12h12M13 7l5 5-5 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>

        <div className="mtf-col mtf-col--right">
          {right.map((item) => {
            const takenBy = rightToLeft[item.id];
            const connected = takenBy != null;
            const isHover = hoverRight === item.id && drag;
            const color = connected ? colorFor(takenBy) : null;
            return (
              <div
                key={item.id}
                ref={setRightRow(item.id)}
                className={
                  "mtf-cell mtf-cell--right" +
                  (connected ? " is-connected" : "") +
                  (isHover ? " is-target" : "")
                }
                style={connected ? { borderColor: color } : undefined}
              >
                <span
                  className="mtf-dot"
                  ref={setRightDot(item.id)}
                  style={connected ? { background: color } : undefined}
                />
                <span className="mtf-label">{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      {showDemoPanel && (
        <>
          {/* Backdrop */}
          <div
            className="mtf-demo-backdrop"
            onClick={() => setShowDemoPanel(false)}
          />

          {/* Right Panel */}
          <div className="mtf-demo-panel">
            <iframe
              src="/match-walkthrough.html"
              title="Match Walkthrough"
              className="mtf-demo-iframe"
            />
            <button
              type="button"
              className="mtf-submit"
              onClick={() => setShowDemoPanel(false)}
            >
              Exit Demo
            </button>
          </div>
        </>
      )}
      <footer className="mtf-foot">
        <span className="mtf-progress">
          {matchedCount} / {Math.min(left.length, right.length)} matched
        </span>
        <div className="mtf-tip">
          <span className="mtf-tip-text">Tip: </span>

          <span className="mtf-tip-link" onClick={() => setShowDemoPanel(true)}>
            Watch the demo
          </span>

          <span className="mtf-tip-text"> to know how to match the terms.</span>
        </div>
        {/* <button
          type="button"
          className="mtf-submit"
          disabled={disabled || matchedCount === 0}
          onClick={() => onSubmit?.(connections)}
        >
          Submit Answer
        </button> */}
      </footer>
    </div>
  );
}

// cubic bezier path string with horizontal control handles
function curve(a, b) {
  const dx = Math.max(40, Math.abs(b.x - a.x) * 0.5);
  return `M ${a.x},${a.y} C ${a.x + dx},${a.y} ${b.x - dx},${b.y} ${b.x},${b.y}`;
}

const LINE_COLORS = [
  "#f6c453", // amber
  "#5ee0a0", // green
  "#6aa6ff", // blue
  "#ff8ec7", // pink
  "#b78bff", // purple
  "#ffa05c", // orange
];

const DEFAULT_LEFT = [
  { id: "service-discovery", label: "Service Discovery" },
  { id: "api-gateway", label: "API Gateway" },
  { id: "circuit-breaker", label: "Circuit Breaker" },
  { id: "load-balancer", label: "Load Balancer" },
  { id: "database-per-service", label: "Database per Service" },
  { id: "health-check", label: "Health Check Endpoint" },
];

const DEFAULT_RIGHT = [
  { id: "distribute-traffic", label: "Distribute traffic across instances" },
  { id: "report-status", label: "Endpoint reporting service status" },
  { id: "own-storage", label: "Own data storage per microservice" },
  { id: "locate-instances", label: "Locate service instances at runtime" },
  { id: "prevent-cascading", label: "Prevent cascading failures" },
  { id: "single-entry", label: "Single entry point for client requests" },
];

// inject styles once
let injected = false;
function StyleOnce() {
  if (injected) return null;
  injected = true;
  return <style dangerouslySetInnerHTML={{ __html: CSS }} />;
}
